import { Composite, Vector } from 'matter-js';
import { SimulationEngine } from '../core/SimulationEngine';
import { BubbleEntity } from '../entities/BubbleEntity';
import { ClusterEntity } from '../entities/ClusterEntity';
import { addClusterConstraintsToWorld, deleteConstraintsFromWorld, recreateClusterConstraints } from '../physics/constraints';
import { BubbleFieldStorage } from '../utils/storage';

export class EntityManager {
    private engine: SimulationEngine;
    private cluster: ClusterEntity | null = null;
    private storage: BubbleFieldStorage;

    constructor(engine: SimulationEngine, storage: BubbleFieldStorage) {
        this.engine = engine;
        this.storage = storage;
    }

    public _CreateClusterFromSelection(bubbles: BubbleEntity[], primaryBubble?: BubbleEntity | null, pointer?: Vector): ClusterEntity | null {
        if (bubbles.length < 1) return null;
        if (this.cluster) {
            // si las burbujas seleccionadas son las mismas y en el mismo orden, no hacer nada
            const currentBubbles = this.cluster._internalBubbles;
            const isSameSelection = bubbles.length === currentBubbles.length
                && primaryBubble === this.cluster._primaryBubble
                && bubbles.every((b, i) => b._id === currentBubbles[i]._id);

            if (isSameSelection) {
                return this.cluster;
            }
            console.log('Cluster actualizado: ' + bubbles.map(b => b._id).join(', '));
            addClusterConstraintsToWorld(
                this.engine,
                recreateClusterConstraints(this.engine, this.cluster, bubbles, primaryBubble)
            );
            return this.cluster;
        }

        const { x, y } = this.calculateClusterCenter(bubbles, primaryBubble, pointer);

        // Cluster
        const cluster = new ClusterEntity({ bubbles, x, y, primaryBubble });

        // Agrega al Engine
        this.engine._AddEntity(cluster);

        // Agregar los "Extras" Físicos.
        // El cluster tiene resortes internos (constraints) que addEntity no ve automáticamente.
        addClusterConstraintsToWorld(this.engine, cluster._GetConstraints());

        // Registro local
        this.cluster = cluster;

        return cluster;
    }

    public _RemoveClusterIfExist() {
        if (!this.cluster) return;
        const lastPosition = this.storage._LastPosition;

        // Liberar las burbujas internas
        deleteConstraintsFromWorld(this.engine, this.cluster._GetConstraints());
        this.cluster._ReleaseBubbles(lastPosition);

        // Eliminar el cluster del motor y del DOM
        this.engine._RemoveEntity(this.cluster._id);

        this.cluster = null; // clear
    }

    public _GetCluster(): ClusterEntity | null {
        return this.cluster;
    }

    public _GetSelectedBubbles(): BubbleEntity[] {
        const selected: BubbleEntity[] = [];
        const bodies = Composite.allBodies(this.engine._engine.world);

        bodies.forEach(body => {
            const entity = body._entity;

            if (entity instanceof BubbleEntity && entity._isSelected) {
                selected.push(entity);
            }
        });

        return selected;
    }

    /**
     * Calcula el punto de nacimiento del cluster.
     * Prioridad: puntero (si se provee) > burbuja primaria > centroide de la selección.
     *
     * Nota: el reordenamiento de `bubbles` (para que la primaria quede al frente) es independiente
     * de qué fuente se use para el centro, ya que antes ambas cosas estaban acopladas y la burbuja
     * primaria terminaba fijando el centro aunque se pidiera centrar en el puntero.
     */
    private calculateClusterCenter(bubbles: BubbleEntity[], primaryBubble?: BubbleEntity | null, pointer?: Vector): Vector {
        if (primaryBubble) {
            const index = bubbles.indexOf(primaryBubble);
            if (index > -1) {
                bubbles.splice(index, 1); // Elimina la burbuja del arreglo
                bubbles.unshift(primaryBubble); // La agrega al inicio del arreglo
            }
        }

        if (pointer) {
            return { x: pointer.x, y: pointer.y };
        }

        if (primaryBubble) {
            return { x: primaryBubble._body.position.x, y: primaryBubble._body.position.y };
        }

        // Calcular Centroide
        let sumX = 0, sumY = 0;

        bubbles.forEach(b => {
            sumX += b._body.position.x;
            sumY += b._body.position.y;
        });

        return { x: sumX / bubbles.length, y: sumY / bubbles.length };
    }
}